import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    Checkbox,
    Grid,
    Icon,
    Select,
    Typography,
} from '@saddlebackchurch/react-cm-ui';
import makeStyles from '@saddlebackchurch/react-cm-ui/core/styles/makeStyles';
import useTheme from '@saddlebackchurch/react-cm-ui/core/styles/useTheme';
import useMediaQuery from '@saddlebackchurch/react-cm-ui/core/utils/useMediaQuery';
import ClassNames from 'classnames';
import {
    isNil,
    noop,
} from 'lodash';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { ServiceType } from '../../../global/api/models';
import { getApiHost } from '../../../global/api/public/utils';
import { SelectOption } from '../../../global/models';
import {
    getAvailableStatementYears,
    requestGivingStatement,
} from '../api/givingHistory.service';
import Button from '../shared/components/button';
import {
    BEM_BLOCK_NAME as ROOT_BEM_BLOCK_NAME,
    GIVING_SADDLEBACK_EMAIL,
    GIVING_SADDLEBACK_PHONE_NUMBER,
    GIVING_SADDLEBACK_PHONE_NUMBER_TEL_LINK,
    NUM_PRIOR_YEARS_STATEMENTS,
} from './constants';

const BEM_SELECTOR_PREFIX = `${ROOT_BEM_BLOCK_NAME}--statements`;

type PropTypes = {
    /**
     * This prop should contain the user's access token,
     * to allow the Download Giving Statement component to make
     * authorized API calls.
     *
     * The user **_MUST_** be authenticated to request Giving Statement downloads.
     */
    accessToken: string;

    /**
     * Boolean flag indicating whether or not to include a spouse's data in Giving History query results
     * and on generated Giving Statements.
     */
    includeSpouseGiving: boolean;

    /**
     * Change handler for `includeSpouseGiving`.
     */
    onChangeIncludeSpouseGiving: (includeSpouseGiving: boolean) => void;

    /**
     * Boolean flag controlling whether or not to use Rock RMS compatible (v4) API endpoints.
     */
    // eslint-disable-next-line react/no-unused-prop-types
    useRockAPIs: boolean;
};

const useStyles = makeStyles(({
    palette,
    spacing,
    typography,
}) => ({
    root: {
        padding: `${spacing(3)}px ${spacing(2)}px`,
        marginBottom: spacing(2),
        backgroundColor: palette.background.primary,
        borderRadius: 5,
    },
    statementButton: {
        backgroundColor: '#54CC86',
        letterSpacing: 'normal',
        paddingTop: typography.pxToRem(13),
        paddingBottom: typography.pxToRem(13),
        paddingLeft: typography.pxToRem(22),
        paddingRight: typography.pxToRem(22),
        '& .button-inner-container': {
            fontSize: typography.pxToRem(14),
            fontWeight: typography.fontWeightMedium,
            lineHeight: 1.125,
        },
    },
    title: {
        fontSize: typography.pxToRem(20),
        lineHeight: 1,
    },
}));

function DownloadStatement({
    accessToken,
    includeSpouseGiving,
    onChangeIncludeSpouseGiving,
}: PropTypes) {
    const classes = useStyles();
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.only('sm'));

    const timeZone = useMemo(() => (moment.tz.guess()), []);
    const givingStatementsApiHost = useMemo(() => getApiHost(ServiceType.Core), []);

    const [isFetchingStatementYears, setIsFetchingStatementYears] = useState<boolean>(false);
    const [isDownloadingStatement, setIsDownloadingStatement] = useState<boolean>(false);
    const [availableStatementYears, setAvailableStatementYears] = useState<SelectOption[]>([]);
    const [selectedStatementYear, setSelectedStatementYear] = useState<number | null>(null);

    const handleIncludeSpouseGivingCheckChanged = useCallback((id: string, checked: boolean) => {
        onChangeIncludeSpouseGiving(checked);
    }, [onChangeIncludeSpouseGiving]);

    const onChangeStatementYear = useCallback((selectedOption: SelectOption) => {
        setSelectedStatementYear(selectedOption.value);
    }, []);

    const onClickPdfStatement = useCallback(() => {
        setIsDownloadingStatement(true);

        requestGivingStatement(
            accessToken,
            selectedStatementYear,
            timeZone,
            includeSpouseGiving,
        )
            .then((response) => {
                const statementPath = '/api/v4/giving/insights/my-giving-statements';
                const statementUrl: string = `${givingStatementsApiHost}${statementPath}?year=${selectedStatementYear}&timeZone=${timeZone}&token=${response.accessToken}&download=true`;
                window.location.href = statementUrl;
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.log('error', error);
            })
            .finally(() => {
                // Set timeout is so that we don't re-enable the button until the download happens
                setTimeout(() => setIsDownloadingStatement(false), 1000);
            });
    }, [
        accessToken,
        givingStatementsApiHost,
        includeSpouseGiving,
        selectedStatementYear,
        timeZone,
    ]);

    // Fetch the available statement years on load
    useEffect(() => {
        if (!isNil(accessToken)) {
            setIsFetchingStatementYears(true);

            getAvailableStatementYears(accessToken, timeZone, includeSpouseGiving)
                .then((years) => {
                    setAvailableStatementYears(years.map((year) => ({
                        label: year.toString(),
                        value: year,
                    })));
                })
                .catch(noop)
                .finally(() => {
                    setIsFetchingStatementYears(false);
                });
        }
    }, [
        accessToken,
        includeSpouseGiving,
        timeZone,
    ]);

    const rootClasses = ClassNames(classes.root, BEM_SELECTOR_PREFIX);
    const titleClasses = ClassNames(classes.title, `${BEM_SELECTOR_PREFIX}_title`);
    const buttonClasses = ClassNames(classes.statementButton, `${BEM_SELECTOR_PREFIX}_button`);

    return (
        <div
            className={rootClasses}
            id="statements"
        >
            <Grid
                className={`${BEM_SELECTOR_PREFIX}_grid`}
                spacing={2}
            >
                <Grid.Column
                    className={`${BEM_SELECTOR_PREFIX}_title_column`}
                    sm={12}
                >
                    <Typography
                        className={titleClasses}
                        variant="h2"
                    >
                        {t('statements.title')}
                    </Typography>
                </Grid.Column>

                <Grid.Column
                    className={`${BEM_SELECTOR_PREFIX}_copy_column`}
                    sm={12}
                >
                    <Typography
                        className={`${BEM_SELECTOR_PREFIX}_copy`}
                        variant="body1"
                    >
                        {t('statements.blurb.main', { numPriorYears: NUM_PRIOR_YEARS_STATEMENTS })}
                        <a href={`mailto:${GIVING_SADDLEBACK_EMAIL}`}>
                            &nbsp;
                            {GIVING_SADDLEBACK_EMAIL}
                            &nbsp;
                        </a>
                        {t('statements.blurb.orAt')}
                        &nbsp;
                        <a href={GIVING_SADDLEBACK_PHONE_NUMBER_TEL_LINK}>
                            {GIVING_SADDLEBACK_PHONE_NUMBER}
                        </a>
                        {t('statements.blurb.end')}
                    </Typography>
                </Grid.Column>

                <Grid.Column
                    className={`${BEM_SELECTOR_PREFIX}_select_column`}
                    md={5}
                    sm={12}
                >
                    <Select
                        className={`${BEM_SELECTOR_PREFIX}_year_select`}
                        disabled={isFetchingStatementYears}
                        fluid
                        id={`${BEM_SELECTOR_PREFIX}_year_select`}
                        onChange={onChangeStatementYear}
                        options={availableStatementYears}
                        placeholder={t('statements.selectYearPlaceholder')}
                        value={selectedStatementYear}
                    />
                </Grid.Column>

                <Grid.Column
                    className={`${BEM_SELECTOR_PREFIX}_button_column`}
                    md={3}
                    sm={12}
                >
                    <Button
                        className={buttonClasses}
                        color="success"
                        disabled={
                            isFetchingStatementYears ||
                            isDownloadingStatement ||
                            isNil(selectedStatementYear)
                        }
                        fullWidth={isMobile}
                        id={`${BEM_SELECTOR_PREFIX}_button`}
                        onClick={onClickPdfStatement}
                        size="lg"
                    >
                        <span>{t('statements.pdfStatementButton')}</span>
                        {
                            isDownloadingStatement && (
                                <Icon
                                    title="false"
                                    type="spinner"
                                />
                            )
                        }
                    </Button>
                </Grid.Column>

                <Grid.Column
                    className={`${BEM_SELECTOR_PREFIX}_include_spouse_giving_option_container`}
                    sm={12}
                >
                    <Checkbox
                        checked={includeSpouseGiving}
                        id={`${BEM_SELECTOR_PREFIX}_include_spouse_giving_checkbox`}
                        label={t('statements.includeSpouseGivingLabel')}
                        onChange={handleIncludeSpouseGivingCheckChanged}
                    />
                </Grid.Column>
            </Grid>
        </div>
    );
}

export default DownloadStatement;
